<template>
  <div class="cv-app">

    <!-- ══════════════════════════════════════
         앱 헤더 (non-sticky, 스크롤과 함께 올라감)
    ══════════════════════════════════════ -->
    <header class="cv-header">
      <!-- 1행: 로고 + 장치 · 날짜 -->
      <div class="cv-header-inner">
        <span class="cv-logo-text">Altimum</span>
        <div class="cv-header-meta">

          <label class="cv-chip cv-chip--select">
            <feather-icon icon="ServerIcon" size="13" />
            <span>{{ currentDeviceName }}</span>
            <select v-model="selectedDevice" class="cv-hidden-native" @change="onDeviceChange">
              <option v-for="d in deviceList" :key="d.id" :value="d.id">{{ d.name }}</option>
            </select>
          </label>

          <label class="cv-chip cv-chip--select">
            <feather-icon icon="CalendarIcon" size="13" />
            <span>{{ selectedDate }}</span>
            <input type="date" v-model="selectedDate" class="cv-hidden-native" @change="onFilterChange" />
          </label>

        </div>
      </div>

      <!-- 2행: 필터 -->
      <div class="cv-header-filter">
        <label class="cv-header-agg">
          <span
            class="cv-agg-badge cv-agg-badge--red"
            :class="{ 'cv-agg-badge--on': selectedClass === null || selectedClass === 'lying' }"
            @click="toggleClassFilter('lying')"
          >전도 {{ lyingCount }}</span>
          <span
            class="cv-agg-badge cv-agg-badge--blu"
            :class="{ 'cv-agg-badge--on': selectedClass === null || selectedClass === 'mounting' }"
            @click="toggleClassFilter('mounting')"
          >승가 {{ mountingCount }}</span>
        </label>

        <label class="cv-chip cv-chip--select cv-chip--filter">
          <feather-icon icon="VideoIcon" size="11" />
          <span>{{ selectedCameraLabel }}</span>
          <select v-model="selectedCamera" class="cv-hidden-native" @change="onFilterChange">
            <option v-for="opt in cameraOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
          </select>
        </label>

        <label class="cv-agg-badge" @click="fetchAlerts">
          <feather-icon icon="RotateCwIcon" size="14" />
        </label>
      </div>

      <!-- 탭 네비게이션 -->
      <nav class="cv-tabs">
        <button
          v-for="tab in tabs" :key="tab.key"
          class="cv-tab"
          :class="{ 'cv-tab--active': activeTab === tab.key }"
          @click="activeTab = tab.key"
          >
          <feather-icon :icon="tab.icon" size="15" />
          <!-- <img src="https://postfiles.pstatic.net/MjAyNjAzMzBfMTQy/MDAxNzc0ODM2Nzk0MzE0.XldpD369KtSxcvW13PgqtTRm1kdRqbd5Izc313s5BnQg.jZyRNoJlHVnMo4mef7Dm2QBDnyUgZAK_7p4qYtddNfsg.PNG/KakaoTalk_20260330_110400314.png?type=w773" class="cv-tab-img" referrerpolicy="no-referrer" alt=""> -->
          <span class="cv-tab-label">{{ tab.label }}</span>
          <span v-if="tab.badge" class="cv-tab-badge">{{ tab.badge }}</span>
        </button>
        <div class="cv-tab-ink" :style="inkStyle" />
      </nav>
    </header>

    <!-- ══════════════════════════════════════
         공통 로딩 / 에러
    ══════════════════════════════════════ -->
    <div v-if="loading" class="cv-fullstate">
      <div class="cv-spinner" />
      <p class="cv-state-text">데이터 로드 중...</p>
    </div>
    <div v-else-if="error" class="cv-fullstate">
      <div  class="cv-state-icon cv-icon--err"><feather-icon icon="YoutubeIcon" size="28" /></div>
      <p class="cv-logo-text">서비스 가입이 필요합니다.</p>
      <a href="https://blog.naver.com/ewhaglobal1" class="cv-ghost-btn">서비스 알아보기 →</a>
    </div>

    <template v-else>

      <!-- ══════════════════════════════════════
           탭 1 : 이벤트 알림
      ══════════════════════════════════════ -->
      <div v-show="activeTab === 'alerts'" class="cv-pane">

        <!-- 빈 상태 -->
        <div v-if="filteredAlerts.length === 0" class="cv-empty">
          <div class="cv-empty-ico"><feather-icon icon="CheckCircleIcon" size="30" /></div>
          <p class="cv-empty-t">이벤트 없음</p>
          <p class="cv-empty-s">선택한 조건에 해당하는 알림이 없습니다</p>
        </div>

        <template v-else>


          <!-- ── Grab 슬라이더 ── -->
          <div
            class="cv-slide-wrap"
            @mousedown="onDragStart"
            @mousemove="onDragMove"
            @mouseup="onDragEnd"
            @mouseleave="onDragEnd"
            @touchstart="onDragStart"
            @touchmove.prevent="onDragMove"
            @touchend="onDragEnd"
          >
            <div class="cv-alert-grid" :style="alertGridStyle">
              <div
                v-for="alert in currentPageAlerts"
                :key="alert.id"
                class="cv-alert-card"
                @click="openFullscreen(alert)"
              >
                <div class="cv-media">
                  <img :src="FILE_BASE + alert.imagePath" class="cv-media-img" @error="onImgError" />
                  <div class="cv-media-overlay">
                    <div class="cv-media-top">
                      <span class="cv-cam-badge">
                        <feather-icon icon="VideoIcon" size="15" />{{ camName(alert.cameraId) }}
                      </span>
                      <span class="cv-cls-badge" :class="alert.className === 'lying' ? 'cv-cls-r' : 'cv-cls-b'">
                        {{ alert.className === 'lying' ? '전도' : '승가' }}
                      </span>
                    </div>
                    <div class="cv-media-btm">
                      <span class="cv-media-type-ico">
                        <feather-icon :icon="alert.className === 'lying' ? 'RadioIcon' : (alert.videoPath ? 'PlayCircleIcon' : 'ImageIcon')" size="25" />
                      </span>
                      <span class="cv-media-time">{{ formatDatetime(alert.detectedAt) }}</span>
                    </div>
                  </div>
                  <div class="cv-stripe" :class="alert.className === 'lying' ? 'cv-stripe-r' : 'cv-stripe-b'" />
                </div>
              </div>
            </div>
          </div>

          <!-- 페이저 -->
          <div class="cv-pager">
            <button class="cv-pager-btn" :disabled="currentPage === 1" @click="goPage(currentPage - 1)">
              <feather-icon icon="ChevronLeftIcon" size="14" />
            </button>

            <div class="cv-pager-track-wrap">
              <!-- 트랙 -->
              <div
                class="cv-pager-track"
                ref="pagerTrack"
                @mousedown="onPagerDragStart"
                @touchstart="onPagerDragStart"
                @mousemove="onPagerDragMove"
                @touchmove.prevent="onPagerDragMove"
                @mouseup="onPagerDragEnd"
                @touchend="onPagerDragEnd"
                @mouseleave="onPagerDragEnd"
                @click="onPagerClick"
              >
                <div class="cv-pager-fill" :style="{ width: pagerFillPct + '%' }" />
                <div class="cv-pager-thumb" :style="{ left: pagerFillPct + '%' }" />
              </div>
              <span class="cv-pager-lbl">{{ currentPage }} / {{ totalPages }}</span>
            </div>

            <button class="cv-pager-btn" :disabled="currentPage === totalPages" @click="goPage(currentPage + 1)">
              <feather-icon icon="ChevronRightIcon" size="14" />
            </button>
          </div>
        </template>
      </div>

      <!-- ══════════════════════════════════════
           탭 2 : CCTV 관리
      ══════════════════════════════════════ -->
      <div v-show="activeTab === 'cameras'" class="cv-pane">
        <div v-if="camLoading" class="cv-fullstate">
          <div class="cv-spinner" /><p class="cv-state-text">CCTV 정보 로드 중...</p>
        </div>
        <div v-else-if="camError" class="cv-fullstate">
          <div class="cv-state-icon cv-icon--err"><feather-icon icon="WifiOffIcon" size="28" /></div>
          <p class="cv-state-text">{{ camError }}</p>
          <button class="cv-ghost-btn" @click="fetchCams">재시도</button>
        </div>
        <template v-else>
          <!-- <div class="cv-sec-hd">
            <div>
              <h2 class="cv-sec-title">CCTV 관리</h2>
              <p class="cv-sec-sub">총 {{ camList.length }}대 · 활성 {{ activeCamCount }}대</p>
            </div>
            <div class="cv-sel-wrap">
              <feather-icon icon="ServerIcon" size="12" class="cv-sel-ico" />
              <b-form-select v-model="selectedDevice" :options="deviceOptions" size="sm" class="cv-sel" @change="onDeviceChange" />
            </div>
          </div> -->
          <div v-if="camList.length === 0" class="cv-empty">
            <div class="cv-empty-ico"><feather-icon icon="CameraOffIcon" size="30" /></div>
            <p class="cv-empty-t">등록된 CCTV 없음</p>
          </div>
          <div v-else class="cv-cam-grid">
            <div v-for="(cam, idx) in camList" :key="cam.id" class="cv-cam-card" :class="{ 'cv-cam-card--off': !cam.isActive }">
              <!-- <div class="cv-cam-vis">
                <span class="cv-cam-num">{{ String(idx + 1).padStart(2,'0') }}</span>
                <div class="cv-cam-ico-wrap"><feather-icon icon="VideoIcon" size="22" /></div>
                <div class="cv-cam-st" :class="cam.isActive ? 'cv-st-on' : 'cv-st-off'">
                  <span class="cv-st-dot" />{{ cam.isActive ? 'LIVE' : 'OFF' }}
                </div>
              </div> -->
              <div class="cv-cam-body" @click="openCamEdit(cam)">
                <div class="cv-cam-name">CH{{idx + 1}} [{{ cam.name }}]</div>
                <div class="cv-cam-pills">
                  <span class="cv-pill" :class="cam.detectLying    ? 'cv-pill-on' : 'cv-pill-off'"><feather-icon icon="ArchiveIcon" size="12" />전도탐지</span>
                  <span class="cv-pill" :class="cam.alertLying     ? 'cv-pill-on' : 'cv-pill-off'"><feather-icon icon="BellIcon"     size="12" />전도알림</span>
                  <span class="cv-pill" :class="cam.detectMounting ? 'cv-pill-on' : 'cv-pill-off'"><feather-icon icon="ArchiveIcon"   size="12" />승가탐지</span>
                  <span class="cv-pill" :class="cam.alertMounting  ? 'cv-pill-on' : 'cv-pill-off'"><feather-icon icon="BellIcon"     size="12" />승가알림</span>
                </div>
                <hr>
                <div class="cv-cam-name">{{ cam.memo }}</div>
              </div>
              <!-- <button class="cv-cam-edit-btn" >
                <feather-icon icon="Edit2Icon" size="13" />편집
              </button> -->
            </div>
          </div>
        </template>
      </div>

      <!-- ══════════════════════════════════════
           탭 3 : 분석
      ══════════════════════════════════════ -->
      <div v-show="activeTab === 'analytics'" class="cv-pane">
        <!-- <div class="cv-sec-hd">
          <div>
            <h2 class="cv-sec-title">이벤트 분석</h2>
            <p class="cv-sec-sub">{{ selectedDate }} 기준 · {{ filteredAlerts.length }}건</p>
          </div>
          <div class="cv-sel-wrap">
            <feather-icon icon="CalendarIcon" size="12" class="cv-sel-ico" />
            <input type="date" class="cv-date-inp" v-model="selectedDate" @change="onFilterChange" />
          </div>
        </div> -->

        <!-- 스탯 카드 -->
        <!-- <div class="cv-stat-grid">
          <div class="cv-stat-card">
            <div class="cv-stat-ico cv-ico-blu"><feather-icon icon="ZapIcon" size="18" /></div>
            <div><div class="cv-stat-n">{{ filteredAlerts.length }}</div><div class="cv-stat-l">이벤트</div></div>
          </div>
          <div class="cv-stat-card">
            <div class="cv-stat-ico cv-ico-red"><feather-icon icon="AlertTriangleIcon" size="18" /></div>
            <div><div class="cv-stat-n cv-n-red">{{ lyingCount }}</div><div class="cv-stat-l">전도 경고</div></div>
          </div>
          <div class="cv-stat-card">
            <div class="cv-stat-ico cv-ico-sky"><feather-icon icon="TrendingUpIcon" size="18" /></div>
            <div><div class="cv-stat-n cv-n-sky">{{ mountingCount }}</div><div class="cv-stat-l">승가 발생</div></div>
          </div>
          <div class="cv-stat-card">
            <div class="cv-stat-ico cv-ico-grn"><feather-icon icon="CameraIcon" size="18" /></div>
            <div><div class="cv-stat-n cv-n-grn"> - </div><div class="cv-stat-l">비활성 CCTV</div></div>
          </div>
        </div> -->

        <!-- 시간대별 추이 (전도=빨강, 승가=파랑) -->
        <div class="cv-chart-card">
          <div class="cv-chart-hd">
            <feather-icon icon="TrendingUpIcon" size="14" class="cv-chart-ico" />시간대 이벤트 발생
            <!-- <div class="cv-chart-legend">
              <span class="cv-legend-dot" style="background:#e53e3e;" /><span class="cv-legend-lbl">전도</span>
              <span class="cv-legend-dot" style="background:#3b82f6;" /><span class="cv-legend-lbl">승가</span>
            </div> -->
          </div>
          <vue-apex-charts type="area" height="220" :options="hourlyOpts" :series="hourlySeries" />
        </div>

        <!-- 카메라 바 선택 시트 -->
        <transition name="cv-sheet-t">
          <div v-if="camSheet.show" class="cv-sheet-backdrop" @click="camSheet.show = false">
            <div class="cv-sheet" @click.stop>
              <div class="cv-sheet-handle" />
              <div class="cv-sheet-head">
                <feather-icon icon="CameraIcon" size="14" class="cv-sheet-ico" />
                <span class="cv-sheet-title">{{ camSheet.name }}</span>
              </div>
              <div class="cv-sheet-stats">
                <div class="cv-sheet-stat cv-sheet-stat--red">
                  <span class="cv-sheet-stat-n">{{ camSheet.lying }}</span>
                  <span class="cv-sheet-stat-l">전도</span>
                </div>
                <div class="cv-sheet-divider" />
                <div class="cv-sheet-stat cv-sheet-stat--blu">
                  <span class="cv-sheet-stat-n">{{ camSheet.mounting }}</span>
                  <span class="cv-sheet-stat-l">승가</span>
                </div>
              </div>
              <div class="cv-sheet-actions">
                <button class="cv-btn cv-btn-ghost" @click="camSheet.show = false">닫기</button>
                <button class="cv-btn cv-btn-pri" @click="goToCamAlerts">
                  영상 보기
                  <feather-icon icon="ArrowRightIcon" size="14" style="margin-left:4px;" />
                </button>
              </div>
            </div>
          </div>
        </transition>

        <!-- CCTV별 + 도넛 -->
        <div class="cv-chart-row">
          <div class="cv-chart-card">
            <div class="cv-chart-hd">
              <feather-icon icon="BarChart2Icon" size="14" class="cv-chart-ico" />CCTV별 이벤트
            </div>
            <vue-apex-charts type="bar" height="220" :options="camBarOpts" :series="camBarSeries" />
          </div>
          <div class="cv-chart-card">
            <div class="cv-chart-hd">
              <feather-icon icon="PieChartIcon" size="14" class="cv-chart-ico" />이벤트 유형 비율
            </div>
            <div v-if="filteredAlerts.length === 0" class="cv-chart-empty">데이터 없음</div>
            <vue-apex-charts v-else type="donut" height="220" :options="donutOpts" :series="donutSeries" />
          </div>
        </div>
      </div>

    </template>

    <!-- ══════════════════════════════════════
         CCTV 편집 모달
    ══════════════════════════════════════ -->
    <transition name="cv-modal-t">
      <div v-if="camEditOpen" class="cv-backdrop" @click.self="closeCamEdit">
        <div class="cv-modal">
          <div class="cv-modal-hd">
            <div class="cv-modal-hd-l">
              <span class="cv-modal-ico"><feather-icon icon="Edit2Icon" size="14" /></span>
              <span class="cv-modal-ttl">CCTV 편집</span>
            </div>
            <button class="cv-modal-x" @click="closeCamEdit"><feather-icon icon="XIcon" size="16" /></button>
          </div>
          <div class="cv-modal-cam-tag">
            <feather-icon icon="VideoIcon" size="12" style="margin-right:6px;opacity:.5;" />{{ editForm.name || 'CCTV' }}
          </div>
          <div v-if="editError" class="cv-modal-err">
            <feather-icon icon="AlertCircleIcon" size="12" style="margin-right:6px;" />{{ editError }}
          </div>
          <div class="cv-modal-bd">
            <div class="cv-field">
              <label class="cv-flbl">CCTV 이름</label>
              <input v-model="editForm.name" type="text" class="cv-finp" placeholder="예) 우사 1번 CCTV" />
            </div>
            <div class="cv-field">
              <label class="cv-flbl">메모</label>
              <textarea v-model="editForm.memo" type="text" class="cv-finp cv-finp-mono" placeholder="메모" />
            </div>
            <div class="cv-tgl-list">
							<div v-for="t in editToggles" :key="t.key" class="cv-tgl-row">
								<div>
									<div class="cv-tgl-name">{{ t.label }}</div>
									<div class="cv-tgl-hint">{{ t.desc }}</div>
								</div>
								<label class="cv-sw">
									<input
										type="checkbox"
										v-model="editForm[t.key]"
										@change="onToggleChange(t.key)"
									/>
									<span class="cv-sw-tr"><span class="cv-sw-th" /></span>
								</label>
							</div>
						</div>
          </div>
          <div class="cv-modal-ft">
            <button class="cv-btn cv-btn-ghost" @click="closeCamEdit">취소</button>
            <button class="cv-btn cv-btn-pri" :disabled="editSaving" @click="saveCamEdit">
              <span v-if="editSaving" class="cv-btn-spin" />
              <feather-icon v-else icon="CheckIcon" size="13" style="margin-right:5px;" />
              {{ editSaving ? '저장 중...' : '저장' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 전체화면 뷰어 -->
    <transition name="cv-fs-t">
      <div v-if="fsOpen" class="cv-fs" @click="fsOpen = false" @touchstart="onFsTouchStart" @touchend="onFsTouchEnd">
        <button class="cv-fs-x" @click.stop="fsOpen = false"><feather-icon icon="XIcon" size="20" /></button>
        <div v-if="fsAlert" class="cv-fs-inner" @click.stop>
          <transition :name="fsSlideClass" mode="out-in">
            <video v-if="fsAlert.videoPath" :key="'v-' + fsAlert.id"
              :src="FILE_BASE + fsAlert.videoPath" :poster="FILE_BASE + fsAlert.imagePath"
              controls autoplay class="cv-fs-media" />
            <img v-else :key="'i-' + fsAlert.id" :src="FILE_BASE + fsAlert.imagePath" class="cv-fs-media" />
          </transition>
          <div class="cv-fs-info">
            <span class="cv-cls-badge" :class="fsAlert.className === 'lying' ? 'cv-cls-r' : 'cv-cls-b'">{{ fsAlert.className === 'lying' ? '전도' : '승가' }}</span>
            <span class="cv-fs-cam">{{ camName(fsAlert.cameraId) }}</span>
            <span class="cv-fs-time">{{ formatDatetime(fsAlert.detectedAt) }}</span>
            <!-- <span class="cv-fs-cnt">{{ fsIndex + 1 }} / {{ filteredAlerts.length }}</span> -->
						<br>
						<span class="cv-fs-cnt">{{ camList[fsAlert.cameraId].memo }}</span>
          </div>
        </div>
        <button class="cv-fs-nav cv-fs-prev" @click.stop="fsMove(-1)" :disabled="fsIndex === 0"><feather-icon icon="ChevronLeftIcon" size="22" /></button>
        <button class="cv-fs-nav cv-fs-next" @click.stop="fsMove(1)"  :disabled="fsIndex === filteredAlerts.length - 1"><feather-icon icon="ChevronRightIcon" size="22" /></button>
      </div>
    </transition>

  </div>
</template>

<script>
import edgeAxios from '../../../libs/edgeAxios'
import dayjs from 'dayjs'
import { getUserData } from '@/auth/utils'
import { BButton, BBadge, BSpinner, BFormSelect, BModal } from 'bootstrap-vue'

const FILE_BASE = 'https://app.e-farm.site/edge-api'
const HLS_BASE  = 'https://app.e-farm.site/hls'
const LOGIN_PW  = '0000'
const TAB_IDX   = {
  alerts: 0,
  analytics: 1,
  cameras: 2,
}

export default {
  name: 'AlertView',
  components: {
    BButton, BBadge, BSpinner, BFormSelect, BModal,
    VueApexCharts: () => import('vue-apexcharts'),
  },
  data() {
    return {
      FILE_BASE, HLS_BASE,
      activeTab: 'alerts',
      loading: true, error: null, token: null,
      deviceList: [], selectedDevice: null,
      camList: [], camMap: {},
      camLoading: false, camError: null,
      alerts: [], selectedCamera: null,
      selectedDate: dayjs().format('YYYY-MM-DD'),
      selectedClass: null, currentPage: 1,
      windowWidth: window.innerWidth, windowHeight: window.innerHeight,
      // drag
      dragging: false, dragStartX: 0, slideDir: '',
      // fullscreen
      liveModalOpen: false, liveAlert: null,
      fsOpen: false, fsAlert: null, fsIndex: 0, fsSlideClass: 'cv-fsl',
      fsTouchStartX: 0,
      // camera edit
      camEditOpen: false, editTarget: null, editError: null, editSaving: false,
      editForm: { name:'', rtspUrl:'', detectLying:false, alertLying:false, detectMounting:false, alertMounting:false },
      camSheet: { show: false, name: '', lying: 0, mounting: 0, camId: null },
      pagerDragging: false,
    }
  },
  computed: {
    // -- 라벨 --
    selectedCameraLabel() {
      if (this.selectedCamera === null) return '전체 CCTV'
      var cam = this.camMap[this.selectedCamera]
      return cam ? cam.name : 'CAM ' + this.selectedCamera
    },
    selectedClassLabel() {
      if (!this.selectedClass) return '전체 이벤트'
      return this.selectedClass === 'lying' ? '전도' : '승가'
    },
    // ── 헤더 ──
    currentDeviceName() {
      var found = null
      for (var i = 0; i < this.deviceList.length; i++) {
        if (this.deviceList[i].id === this.selectedDevice) { found = this.deviceList[i]; break }
      }
      return found ? found.name : '장치'
    },

    // ── 탭 ──
    tabs() {
      var b = this.lyingCount + this.mountingCount
      return [
        { key:'alerts',    label:'알림',  icon:'BellIcon',      badge: b || null },
        { key:'analytics', label:'분석',  icon:'BarChart2Icon', badge: null },
        { key:'cameras',   label:'관리',  icon:'SettingsIcon',    badge: null },
      ]
    },
    inkStyle() {
      var idx = TAB_IDX[this.activeTab] || 0
      var w = 100 / 3
      return { left: (idx * w) + '%', width: w + '%' }
    },

    // ── 그리드 레이아웃 ──
    cols() {
      if (this.windowWidth >= 1024) return 4
      if (this.windowWidth >= 640)  return 2
      return 1
    },
    rows() {
      var usable = this.windowHeight - 220
      var maxW   = this.windowWidth > 1600 ? 1600 : this.windowWidth
      var colW   = (maxW - 32) / this.cols
      var cardH  = colW * 9 / 16
      var r      = Math.floor(usable / (cardH + 12))
      var maxR   = this.windowWidth < 768 ? 3 : 6
      return r < 1 ? 1 : (r > maxR ? maxR : r)
    },
    perPage() { return this.cols * this.rows },
    alertGridStyle() {
      var maxW = this.windowWidth > 1600 ? 1600 : this.windowWidth
      var colW = (maxW - 32) / this.cols
      return {
        gridTemplateColumns: 'repeat(' + this.cols + ', 1fr)',
        gridAutoRows: (colW * 9 / 16) + 'px',
      }
    },

    // ── 셀렉트 옵션 ──
    deviceOptions() {
      return this.deviceList.map(function(d) { return { value: d.id, text: d.name } })
    },
    cameraOptions() {
      var ids = []
      var seen = {}
      this.alerts.forEach(function(a) { if (!seen[a.cameraId]) { seen[a.cameraId] = true; ids.push(a.cameraId) } })
      ids.sort(function(a, b) { return a - b })
      var self = this
      var opts = [{ value: null, text: '전체 보기' }]
      ids.forEach(function(id) { opts.push({ value: id, text: self.camMap[id] ? self.camMap[id].name : 'CAM ' + id }) })
      return opts
    },
    classOptions() {
      return [{ value:null, text:'전체' }, { value:'lying', text:'전도' }, { value:'mounting', text:'승가' }]
    },

    // ── 필터 ──
    filteredAlerts() {
      return this.alerts.filter(function(a) {
        if (this.selectedCamera !== null && a.cameraId !== this.selectedCamera) return false
        if (this.selectedClass && a.className !== this.selectedClass) return false
        return true
      }.bind(this))
    },
    lyingCount()    { return this.filteredAlerts.filter(function(a) { return a.className === 'lying' }).length },
    mountingCount() { return this.filteredAlerts.filter(function(a) { return a.className === 'mounting' }).length },
    uniqueCamCount() {
      var seen = {}
      this.filteredAlerts.forEach(function(a) { seen[a.cameraId] = true })
      return Object.keys(seen).length
    },
    activeCamCount() {
      return this.camList.filter(function(c) { return c.isActive }).length
    },
    totalPages() {
      var t = Math.ceil(this.filteredAlerts.length / this.perPage)
      return t < 1 ? 1 : t
    },
    dotPages() { return this.totalPages < 9 ? this.totalPages : 9 },

    // ── 페이지별 데이터 ──
    pagerFillPct() {
      if (this.totalPages <= 1) return 0
      return ((this.currentPage - 1) / (this.totalPages - 1)) * 100
    },
    currentPageAlerts() {
      var s = (this.currentPage - 1) * this.perPage
      return this.filteredAlerts.slice(s, s + this.perPage)
    },
    prevPageAlerts() {
      if (this.currentPage <= 1) return []
      var s = (this.currentPage - 2) * this.perPage
      return this.filteredAlerts.slice(s, s + this.perPage)
    },
    nextPageAlerts() {
      if (this.currentPage >= this.totalPages) return []
      var s = this.currentPage * this.perPage
      return this.filteredAlerts.slice(s, s + this.perPage)
    },

    // ── 드래그 트랙 style ──
    trackStyle() {
      var dragPct = this.windowWidth > 0 ? (this.dragOffsetX / this.windowWidth) * 100 : 0
      // 첫/마지막 페이지 경계에서 저항감
      if ((this.currentPage === 1 && this.dragOffsetX > 0) ||
          (this.currentPage === this.totalPages && this.dragOffsetX < 0)) {
        dragPct = dragPct * 0.15
      }
      return {
        transform: 'translateX(' + (-100 + dragPct) + '%)',
        transition: this.dragging ? 'none' : 'transform .32s cubic-bezier(.25,.46,.45,.94)',
        willChange: 'transform',
      }
    },

    // ── 분석 차트 ──
    hourlyData() {
      var arr = []
      for (var i = 0; i < 24; i++) arr.push({ h: i, lying: 0, mounting: 0 })
      this.filteredAlerts.forEach(function(a) {
        var h = dayjs(a.detectedAt).hour()
        if (a.className === 'lying') arr[h].lying++
        else if (a.className === 'mounting') arr[h].mounting++
      })
      return arr
    },
    hourlySeries() {
      return [
        { name: '전도', data: this.hourlyData.map(function(d) { return d.lying }) },
        { name: '승가', data: this.hourlyData.map(function(d) { return d.mounting }) },
      ]
    },
    hourlyOpts() {
      return {
        chart: {
          type:'area',
          toolbar: { show: false },
          zoom:    { enabled: false },
          background:'transparent',
          animations:{ speed:600 },
          offsetX: -10,
          offsetY: 0,
        },
        colors: ['#e53e3e', '#3b82f6'],
        fill: { type:'gradient', gradient:{ opacityFrom:.28, opacityTo:.02 } },
        stroke: { curve:'smooth', width:2 },
        xaxis: {
          categories: this.hourlyData.map(function(d) { return (d.h < 10 ? '0' : '') + d.h + '시' }),
          tickAmount: 6,
          labels: { style: { colors:'#8892aa', fontSize:'13px' } },
          axisBorder: { show:false }, axisTicks: { show:false },
        },
        yaxis: { min:0, labels: { style:{ colors:'#8892aa', fontSize:'15px' }, formatter: function(v) { return Math.floor(v) } }, minWidth: 0, maxWidth: 28, },
        grid: {
          borderColor:'#e8ecf4',
          strokeDashArray:4,
          padding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 20,
          },
        },
        legend: { labels:{ colors:'#5a6179' }, fontSize:'15px' },
        noData: { text:'데이터 없음', style:{ color:'#9aa0b8' } },
        dataLabels: {
          enabled: true,
          formatter: function(value, opts) {
            var series = opts.w.globals.series[opts.seriesIndex]
            var i = opts.dataPointIndex
            var prev = i > 0 ? series[i - 1] : -1
            var next = i < series.length - 1 ? series[i + 1] : -1
            if (value > 0 && value > prev && value > next) return value
            return ''
          },
          style: { fontSize: '15px', colors: ['#e53e3e', '#3b82f6'] },
          background: { enabled: false },
          offsetY: -4,
        },
      }
    },
    camBarCategories() {
      var ids = []
      var seen = {}
      this.filteredAlerts.forEach(function(a) { if (!seen[a.cameraId]) { seen[a.cameraId] = true; ids.push(a.cameraId) } })
      ids.sort(function(a, b) { return a - b })
      var self = this
      return ids.map(function(id) { return self.camMap[id] ? self.camMap[id].name : 'CAM ' + id })
    },
    camBarIds() {
      var ids = []; var seen = {}
      this.filteredAlerts.forEach(function(a) { if (!seen[a.cameraId]) { seen[a.cameraId] = true; ids.push(a.cameraId) } })
      ids.sort(function(a, b) { return a - b })
      return ids
    },
    camBarSeries() {
      var self = this
      return [
        { name: '전도', data: this.camBarIds.map(function(id) { return self.filteredAlerts.filter(function(a) { return a.cameraId === id && a.className === 'lying' }).length }) },
        { name: '승가', data: this.camBarIds.map(function(id) { return self.filteredAlerts.filter(function(a) { return a.cameraId === id && a.className === 'mounting' }).length }) },
      ]
    },
    camBarOpts() {
      var self = this   // ← 여기서 캡처
      return {
        chart: {
          type:'bar',
          toolbar: { show: false },
          zoom:    { enabled: false },
          background:'transparent',
          animations:{ speed:600 },
          offsetX: -10, offsetY: 0,
          events: {
            dataPointSelection: function(event, chartContext, config) {
              var idx     = config.dataPointIndex
              var id      = self.camBarIds[idx]
              var lying   = self.filteredAlerts.filter(function(a) { return a.cameraId === id && a.className === 'lying' }).length
              var mounting= self.filteredAlerts.filter(function(a) { return a.cameraId === id && a.className === 'mounting' }).length
              self.camSheet = {
                show:     true,
                camId:    id,
                name:     self.camMap[id] ? self.camMap[id].name : 'CAM ' + id,
                lying:    lying,
                mounting: mounting,
              }
            }
          }
        },
        colors: ['#e53e3e', '#3b82f6'],
        plotOptions: { bar:{ borderRadius:4, columnWidth:'55%' } },
        dataLabels: {
          enabled: true,
          formatter: function(value, opts) {
            var series = opts.w.globals.series[opts.seriesIndex]
            var i = opts.dataPointIndex
            var prev = i > 0 ? series[i - 1] : -1
            var next = i < series.length - 1 ? series[i + 1] : -1
            if (value > 0 && value > prev && value > next) return value
            return ''
          },
          style: { fontSize: '15px', colors: ['#000000', '#000000'] },
          background: { enabled: false },
          offsetY: 0,
        },
        xaxis: {
          categories: this.camBarCategories,
          labels: { style:{ colors:'#8892aa', fontSize:'12px' } },
          axisBorder:{ show:false }, axisTicks:{ show:false },
        },
        yaxis: {
          min:0,
          labels: {
            style: { colors:'#8892aa', fontSize:'12px' },
            formatter: function(v) { return Math.floor(v) },
            minWidth: 0,
            maxWidth: 28,
          },
        },
        grid: { borderColor:'#e8ecf4', strokeDashArray:4,
          padding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 20,
          },
        },
        legend: { labels:{ colors:'#5a6179' }, fontSize:'12px' },
        tooltip: {
          theme:'light',
          y:{ formatter: function(v) { return v + '건' } },
        },
        noData: { text:'데이터 없음', style:{ color:'#9aa0b8' } },
      }
    },
    donutSeries() { return [this.lyingCount, this.mountingCount] },
    donutOpts() {
      return {
        chart: { type:'donut', background:'transparent', animations:{ speed:600 } },
        colors: ['#e53e3e', '#3b82f6'],
        labels: ['전도', '승가'],
        plotOptions: { pie:{ donut:{ size:'65%', labels:{ show:true,
          total:{ show:true, label:'합계', color:'#5a6179',
            formatter: function(w) { return w.globals.seriesTotals.reduce(function(a, b) { return a + b }, 0) + '건' } } } } } },
        legend: { position:'bottom', labels:{ colors:'#5a6179' }, fontSize:'12px' },
        dataLabels: { style:{ fontSize:'12px' } },
        tooltip: { y:{ formatter: function(v) { return v + '건' } } },
      }
    },

    // ── 편집 토글 목록 ──
    editToggles() {
      return [
        { key:'detectLying',    label:'전도 탐지', desc:'AI가 소 전도 행동을 감지합니다' },
        { key:'alertLying',     label:'전도 알림', desc:'전도 감지 시 알림을 전송합니다' },
        { key:'detectMounting', label:'승가 탐지', desc:'AI가 승가 행동을 감지합니다' },
        { key:'alertMounting',  label:'승가 알림', desc:'승가 감지 시 알림을 전송합니다' },
      ]
    },
  },

  async mounted() {
    window.addEventListener('resize', this.onResize)
    await this.init()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },

  methods: {
    // ── 유틸 ──
    camName(cameraId) {
      var cam = this.camMap[cameraId]
      return cam && cam.name ? cam.name : 'CAM ' + cameraId
    },
    onResize() {
      this.windowWidth  = window.innerWidth
      this.windowHeight = window.innerHeight
      this.currentPage  = 1
    },
    formatDatetime(dt) { return dayjs(dt).format('MM.DD HH:mm') },
    onImgError(e) { e.target.src = FILE_BASE + '/uploads/load.png' },

    // ── 초기화 ──
    async init() {
      this.loading = true; this.error = null
      try {
        var u = await getUserData()
        await this.login(u.phone)
        await this.fetchDevices()
        await this.fetchCams()
        await this.fetchAlerts()
      } catch(e) { this.error = e.message || '초기화 실패' }
      finally { this.loading = false }
    },
    async login(phone) {
      var passwd = LOGIN_PW
      if (phone === '62024102' || phone === '33') { phone = 'develop'; passwd = 'ewha2110' }
      var res = await edgeAxios.post('/api/auth/login', { userId: phone, password: passwd })
      this.token = res.data.token
    },
    async fetchDevices() {
      var res = await edgeAxios.get('/api/devices', { headers: { Authorization: 'Bearer ' + this.token } })
      this.deviceList = Array.isArray(res.data) ? res.data : [res.data]
      if (this.deviceList.length) this.selectedDevice = this.deviceList[0].id
    },
    async onDeviceChange() {
      this.selectedCamera = null; this.currentPage = 1
      await this.fetchCams(); await this.fetchAlerts()
    },
    onFilterChange() { this.currentPage = 1; this.fetchAlerts() },
    toggleClassFilter(cls) {

      const befordClass = this.selectedClass
      const afterClass = cls

      if(befordClass === null) this.selectedClass = afterClass === 'lying' ? 'mounting' : 'lying'
      else {
        if(befordClass === afterClass) this.selectedClass = afterClass === 'lying' ? 'mounting' : 'lying'
        else this.selectedClass = null
      }
      this.onFilterChange()
    },
    async fetchAlerts() {
      if (!this.selectedDevice) return
      try {
        var res = await edgeAxios.get('/api/alerts/' + this.selectedDevice + '/' + this.selectedDate, { headers: { Authorization: 'Bearer ' + this.token } })
        this.alerts = Array.isArray(res.data) ? res.data : []
        this.currentPage = 1
      } catch(e) { this.alerts = [] }
    },
    async fetchCams() {
      if (!this.selectedDevice) return
      this.camLoading = true; this.camError = null
      try {
        var res = await edgeAxios.get('/api/cameras/' + this.selectedDevice, { headers: { Authorization: 'Bearer ' + this.token } })
        this.camList = Array.isArray(res.data) ? res.data : []
        this.camMap = {}
        this.camList.forEach(function(cam, idx) { this.camMap[idx] = cam }.bind(this))
      } catch(e) { this.camList = []; this.camMap = {}; this.camError = e.message || 'CCTV 로드 실패' }
      finally { this.camLoading = false }
    },

    // ── 페이지 이동 ──
    goPage(p) {
      if (p < 1 || p > this.totalPages || p === this.currentPage) return
      this.slideDir   = p > this.currentPage ? 'left' : 'right'
      this.currentPage = p
      var self = this
      this.$nextTick(function() {
        var el = self.$el.querySelector('.cv-alert-grid')
        if (!el) return
        el.classList.add('cv-slide-in-' + self.slideDir)
        setTimeout(function() { el.classList.remove('cv-slide-in-left', 'cv-slide-in-right') }, 350)
      })
    },
    onPagerDragStart(e) {
      this.pagerDragging = true
      this.onPagerDragMove(e)
    },
    onPagerDragMove(e) {
      if (!this.pagerDragging) return
      var track = this.$refs.pagerTrack
      if (!track) return
      var rect  = track.getBoundingClientRect()
      var x     = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left
      var pct   = Math.max(0, Math.min(1, x / rect.width))
      var page  = Math.round(pct * (this.totalPages - 1)) + 1
      if (page !== this.currentPage) this.currentPage = page
    },
    onPagerDragEnd(e) {
      this.pagerDragging = false
    },
    onPagerClick(e) {
      this.pagerDragging = true
      this.onPagerDragMove(e)
      this.pagerDragging = false
    },

    // ── Grab 드래그 ──
    onDragStart(e) {
      this.dragging   = true
      this.dragStartX = e.touches ? e.touches[0].clientX : e.clientX
    },
    onDragMove(e) {
      // if (!this.dragging) return
      // var x = e.touches ? e.touches[0].clientX : e.clientX
      // this.dragOffsetX = x - this.dragStartX
    },
    onDragEnd(e) {
      if (!this.dragging) return
      this.dragging = false
      var endX      = e.changedTouches ? e.changedTouches[0].clientX : e.clientX
      var dx        = endX - this.dragStartX
      if (Math.abs(dx) < 40) return
      if (dx < 0) this.goPage(this.currentPage + 1)
      else        this.goPage(this.currentPage - 1)
    },

    // ── CCTV 편집 ──
    openCamEdit(cam) {
      this.editTarget = cam
      this.editForm = {
        name: cam.name,
        rtspUrl: cam.rtspUrl,
        memo: cam.memo,
        detectLying: !!cam.detectLying,
        alertLying: !!cam.alertLying,
        detectMounting: !!cam.detectMounting,
        alertMounting: !!cam.alertMounting,
      }
      console.log(this.editForm)
      this.editError = null; this.camEditOpen = true
    },
    closeCamEdit() { this.camEditOpen = false; this.editTarget = null; this.editError = null },
    async saveCamEdit() {
      if (!this.editForm.name.trim())    { this.editError = 'CCTV 이름을 입력해주세요.'; return }
      if (!this.editForm.rtspUrl.trim()) { this.editError = 'RTSP 주소를 입력해주세요.'; return }
      this.editSaving = true; this.editError = null
      try {
        const res = await edgeAxios.put(
          `/api/cameras/${this.editTarget.edgeDeviceId}/${this.editTarget.id}`,
          this.editForm,
          { headers: { Authorization: 'Bearer ' + this.token } }
        )

        var i = this.camList.findIndex(function(c) { return c.id === this.editTarget.id }.bind(this))
        if (i !== -1) this.$set(this.camList, i, Object.assign({}, this.camList[i], this.editForm))
        this.closeCamEdit()
        await this.fetchCams()
      } catch(e) {
        this.editError = (e && e.response && e.response.data && e.response.data.message) ? e.response.data.message : '저장에 실패했습니다.'
      } finally { this.editSaving = false }
    },
    goToCamAlerts() {
      this.selectedCamera = this.camSheet.camId
      this.currentPage    = 1
      this.activeTab      = 'alerts'
      this.camSheet.show  = false
      this.$nextTick(function() {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
    },

    // ── 전체화면 ──
    openFullscreen(alert) {
      if (alert.className === 'lying') {
        var cam = this.camList[alert.cameraId]
        this.fsIndex = this.filteredAlerts.findIndex(function(a) { return a.id === alert.id })
        this.fsAlert = this.filteredAlerts[this.fsIndex]
        this.fsOpen  = true
        // this.liveAlert = Object.assign({}, alert, { streamKey: cam ? cam.streamKey : null, imagePath: FILE_BASE + alert.imagePath })
        // this.liveModalOpen = true
      } else {
        this.fsIndex = this.filteredAlerts.findIndex(function(a) { return a.id === alert.id })
        this.fsAlert = this.filteredAlerts[this.fsIndex]
        this.fsOpen  = true
      }
    },
    fsMove(dir) {
      var next = this.fsIndex + dir
      if (next < 0 || next >= this.filteredAlerts.length) return
      this.fsSlideClass = dir > 0 ? 'cv-fsl' : 'cv-fsr'
      this.fsIndex = next; this.fsAlert = this.filteredAlerts[next]
    },
    onFsTouchStart(e) { this.fsTouchStartX = e.changedTouches[0].clientX },
    onFsTouchEnd(e) {
      var dx = e.changedTouches[0].clientX - this.fsTouchStartX
      if (Math.abs(dx) > 50) { e.stopPropagation(); this.fsMove(dx < 0 ? 1 : -1) }
    },
		onToggleChange(key) {
			console.log(key)
			if (key === 'detectLying' && !this.editForm.detectLying) {
				this.editForm.alertLying = false;
			}
			if (key === 'detectMounting' && !this.editForm.detectMounting) {
				this.editForm.alertMounting = false;
			}
		}
  },
}
</script>

<style scoped>
/* ═══════════════════════════════════════════════════
   디자인 토큰
═══════════════════════════════════════════════════ */
.cv-app {
  --bg:      #f0f3fa;
  --srf:     #ffffff;
  --brd:     #e2e6f0;
  --brd-sub: #edf0f8;
  --hd:      #0d1120;
  --hd-t:    #c8d0e8;
  --pri:     #4f46e5;
  --pri-lt:  #eef2ff;
  --red:     #e53e3e;
  --red-lt:  #fff5f5;
  --sky:     #3b82f6;
  --sky-lt:  #eff6ff;
  --grn:     #16a34a;
  --grn-lt:  #f0fdf4;
  --t1:      #0d1120;
  --t2:      #4a5179;
  --t3:      #8892aa;
  --r:       12px;
  --sh-sm:   0 2px 8px rgba(13,17,32,.06);
  --sh-md:   0 6px 24px rgba(13,17,32,.10);

  display: flex; flex-direction: column; min-height: 100vh;
  background: var(--bg);
  font-family: 'Pretendard', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
}

/* ═══════════════════════════════
   헤더 (라이트 테마)
═══════════════════════════════ */
.cv-header {
  background: #fff;
  border-bottom: 1px solid var(--brd);
  flex-shrink: 0;
  box-shadow: 0 2px 12px rgba(79,70,229,.08);
}
.cv-header-inner {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 16px 5px;
}
.cv-logo-text {
  font-size: 1.1rem; font-weight: 900;
  letter-spacing: .03em;
  color: var(--pri);
  font-style: italic;
}
.cv-header-meta { display: flex; align-items: center; gap: 6px; }

/* 칩 */
.cv-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 11px; border-radius: 20px;
  font-size: .72rem; font-weight: 700;
  background: var(--pri-lt); color: var(--pri);
  border: 1.5px solid rgba(79,70,229,.15);
  transition: all .15s;
}
.cv-chip--select {
  position: relative; cursor: pointer;
}
.cv-chip--select:hover {
  background: #e0e7ff;
  border-color: var(--pri);
}
.cv-hidden-native {
  position: absolute; inset: 0; opacity: 0;
  width: 100%; height: 100%; cursor: pointer; font-size: 16px;
}

.cv-tab-img {
  width: 100%;
}

/* 2행 필터 */
.cv-header-filter {
  display: flex; align-items: center; gap: 6px;
  padding: 1px 16px 10px; flex-wrap: wrap;
  justify-content: flex-end;
}
.cv-chip--filter {
  background: #f8f9ff;
  border-color: var(--brd);
  color: var(--t2);
}
.cv-chip--filter:hover {
  background: var(--pri-lt);
  border-color: var(--pri);
  color: var(--pri);
}

/* 집계 뱃지 */
.cv-header-agg { display: flex; align-items: center; gap: 5px; }
.cv-agg-badge {
  font-size: .72rem; font-weight: 700;
  padding: 4px 10px; border-radius: 20px;
  border: 1.5px solid var(--brd);
  background: #f8f9ff; color: var(--t3);
  cursor: pointer; transition: all .15s;
  user-select: none;
}
.cv-agg-badge--red.cv-agg-badge--on {
  background: var(--red-lt); color: var(--red);
  border-color: rgba(229,62,62,.3);
}
.cv-agg-badge--blu.cv-agg-badge--on {
  background: var(--sky-lt); color: var(--sky);
  border-color: rgba(59,130,246,.3);
}
.cv-agg-badge--red:hover { background: var(--red-lt); color: var(--red); border-color: rgba(229,62,62,.3); }
.cv-agg-badge--blu:hover { background: var(--sky-lt); color: var(--sky); border-color: rgba(59,130,246,.3); }

/* 새로고침 버튼 */
.cv-refresh-btn {
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 50%;
  border: 1.5px solid var(--brd); background: #f8f9ff;
  color: var(--t3); cursor: pointer;
  transition: all .15s; margin-left: auto;
}
.cv-refresh-btn:hover {
  background: var(--pri-lt); color: var(--pri);
  border-color: var(--pri);
}

/* 탭 */
.cv-tabs {
  display: grid; grid-template-columns: repeat(3,1fr);
  position: relative;
  border-top: 1px solid var(--brd);
}
.cv-tab {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px 6px; background: none; border: none;
  font-size: .82rem; font-weight: 600; color: var(--t3);
  cursor: pointer; transition: color .2s; position: relative; z-index: 1;
}
.cv-tab--active { color: var(--pri); }
.cv-tab-badge {
  background: var(--red); color: #fff;
  font-size: 9px; font-weight: 800; padding: 1px 5px; border-radius: 10px;
}
.cv-tab-ink {
  position: absolute; bottom: 0; height: 2px;
  background: var(--pri);
  border-radius: 2px 2px 0 0;
  transition: left .25s cubic-bezier(.4,0,.2,1), width .25s cubic-bezier(.4,0,.2,1);
}

/* ═══════════════════════════════════════════════════
   공통 상태
═══════════════════════════════════════════════════ */
.cv-pane { flex: 1; padding-bottom: 80px; }
.cv-fullstate { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 55vh; gap: 12px; }
.cv-spinner { width: 42px; height: 42px; border-radius: 50%; border: 3px solid var(--brd); border-top-color: var(--pri); animation: cvSpin .75s linear infinite; }
@keyframes cvSpin { to { transform: rotate(360deg); } }
.cv-state-text { font-size: .86rem; color: var(--t3); }
.cv-state-icon { width: 56px; height: 56px; border-radius: 16px; display: flex; align-items: center; justify-content: center; }
.cv-icon--err { background: var(--red-lt); color: var(--red); }
.cv-ghost-btn {
  display: inline-flex; align-items: center; padding: 8px 20px;
  border-radius: 8px; font-size: .82rem; font-weight: 700;
  background: var(--pri-lt); color: var(--pri); border: none; cursor: pointer;
  text-decoration: none; transition: background .15s;
}
.cv-ghost-btn:hover { background: #dde4ff; }
.cv-empty { display: flex; flex-direction: column; align-items: center; padding: 60px 20px; gap: 10px; }
.cv-empty-ico { width: 58px; height: 58px; border-radius: 16px; background: var(--brd-sub); color: var(--t3); display: flex; align-items: center; justify-content: center; }
.cv-empty-t { font-size: .95rem; font-weight: 700; color: var(--t2); margin: 0; }
.cv-empty-s { font-size: .78rem; color: var(--t3); text-align: center; margin: 0; }
.cv-sec-hd { display: flex; align-items: flex-start; justify-content: space-between; padding: 18px 16px 12px; gap: 12px; flex-wrap: wrap; }
.cv-sec-title { font-size: 1.05rem; font-weight: 800; color: var(--t1); margin: 0 0 2px; }
.cv-sec-sub { font-size: .76rem; color: var(--t3); margin: 0; }

/* ═══════════════════════════════════════════════════
   공통 컨트롤
═══════════════════════════════════════════════════ */
.cv-sel-wrap {
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--srf); border: 1px solid var(--brd);
  border-radius: 9px; padding: 4px 4px 4px 4px; min-width: 58px;
}
.cv-sel-ico { color: var(--t3); flex-shrink: 0; }
.cv-sel { border: none !important; background: transparent !important; font-size: .59rem; color: var(--t2); font-weight: 600; padding: 0 !important; box-shadow: none !important; }
.cv-date-inp { border: none; background: transparent; font-size: .79rem; color: var(--t2); font-weight: 600; outline: none; padding: 0; }

/* ═══════════════════════════════════════════════════
   탭1 — 이벤트 알림
═══════════════════════════════════════════════════ */
.cv-filter-bar { background: var(--srf); border-bottom: 1px solid var(--brd); padding: 8px 12px; }
.cv-filter-row { display: flex; align-items: center; gap: 6px; flex-wrap: nowrap; }
.cv-agg-inline { display: flex; align-items: center; gap: 5px; margin-left: auto; flex-shrink: 0; }
.cv-agg-chip {
  display: flex; align-items: center; gap: 4px;
  padding: 5px 10px; border-radius: 8px;
  border: 1.5px solid var(--brd); background: var(--srf);
  cursor: pointer; transition: border-color .15s, background .15s;
}
.cv-agg--total { cursor: default; }
.cv-agg--lying:hover  { border-color: var(--red); background: var(--red-lt); }
.cv-agg--mounting:hover { border-color: var(--sky); background: var(--sky-lt); }
.cv-agg--on-red { border-color: var(--red) !important; background: var(--red-lt) !important; }
.cv-agg--on-blu { border-color: var(--sky) !important; background: var(--sky-lt) !important; }
.cv-agg-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.cv-dot-r { background: var(--red); }
.cv-dot-b { background: var(--sky); }
.cv-agg-n { font-size: .88rem; font-weight: 800; color: var(--t1); line-height: 1; font-variant-numeric: tabular-nums; }
.cv-agg-l { font-size: .62rem; font-weight: 600; color: var(--t3); }

/* 페이저 */
.cv-pager {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px 4px;
}
.cv-pager-btn {
  width: 28px; height: 28px; border-radius: 8px;
  border: 1px solid var(--brd); background: var(--srf); color: var(--t2);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all .15s; flex-shrink: 0;
}
.cv-pager-btn:disabled { opacity: .3; cursor: default; }
.cv-pager-btn:not(:disabled):hover { background: var(--pri-lt); color: var(--pri); border-color: var(--pri); }

.cv-pager-track-wrap {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; gap: 6px;
}
.cv-pager-track {
  width: 100%; height: 20px;
  display: flex; align-items: center;
  position: relative; cursor: pointer;
  padding: 0 8px;
}
.cv-pager-track::before {
  content: ''; position: absolute;
  left: 8px; right: 8px; top: 50%; transform: translateY(-50%);
  height: 3px; border-radius: 2px;
  background: var(--brd);
}
.cv-pager-fill {
  position: absolute; left: 8px; top: 50%; transform: translateY(-50%);
  height: 3px; border-radius: 2px;
  background: var(--pri);
  pointer-events: none;
  transition: width .1s;
}
.cv-pager-thumb {
  position: absolute; top: 50%; transform: translate(-50%, -50%);
  width: 16px; height: 16px; border-radius: 50%;
  background: var(--pri);
  box-shadow: 0 0 0 3px rgba(79,70,229,.2);
  pointer-events: none;
  transition: left .1s, box-shadow .15s;
}
.cv-pager-track:active .cv-pager-thumb {
  box-shadow: 0 0 0 5px rgba(79,70,229,.25);
}
.cv-pager-lbl {
  font-size: .74rem; font-weight: 700; color: var(--t3);
  font-variant-numeric: tabular-nums;
}

/* Grab 슬라이더 */
.cv-slide-wrap {
  overflow: hidden;
  padding: 5px 5px 0;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
}
.cv-slide-wrap:active { cursor: grabbing; }
.cv-alert-grid { display: grid; gap: 10px; }
.cv-alert-card {
  border-radius: 10px; overflow: hidden;
  box-shadow: var(--sh-sm); cursor: pointer;
  transition: transform .18s, box-shadow .18s;
  position: relative;
}
.cv-slide-in-left {
  animation: cvSlideInLeft .32s cubic-bezier(.25,.46,.45,.94) both;
}
.cv-slide-in-right {
  animation: cvSlideInRight .32s cubic-bezier(.25,.46,.45,.94) both;
}
@keyframes cvSlideInLeft {
  from { transform: translateX(60px); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
}
@keyframes cvSlideInRight {
  from { transform: translateX(-60px); opacity: 0; }
  to   { transform: translateX(0);     opacity: 1; }
}
.cv-alert-card:hover { transform: translateY(-3px); box-shadow: var(--sh-md); }
.cv-media { position: relative; width: 100%; height: 100%; background: #111; font-size: 0.88rem;  }
.cv-media-img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .35s; }
.cv-alert-card:hover .cv-media-img { transform: scale(1.03); }
.cv-media-overlay {
  position: absolute; inset: 0;
  display: flex; flex-direction: column; justify-content: space-between; padding: 8px;
  background: linear-gradient(180deg,rgba(0,0,0,.55) 0%,transparent 38%,transparent 56%,rgba(0,0,0,.72) 100%);
  pointer-events: none;
}
.cv-media-top, .cv-media-btm { display: flex; align-items: center; justify-content: space-between; gap: 4px; }
.cv-cam-badge {
  display: inline-flex; align-items: center; gap: 4px;
  background: rgba(0,0,0,.65); color: #e8ecf4;
  font-size: .8rem; font-weight: 700; padding: 3px 8px; border-radius: 6px;
  backdrop-filter: blur(4px);
}
.cv-cls-badge { font-size: .68rem; font-weight: 800; padding: 3px 9px; border-radius: 6px; letter-spacing: .03em; }
.cv-cls-r { background: rgba(229,62,62,.9);  color: #fff; }
.cv-cls-b { background: rgba(59,130,246,.9); color: #fff; }
.cv-media-type-ico { color: rgba(255,255,255,.8); }
.cv-media-time {
  font-size: .77rem; font-weight: 600; color: rgba(255,255,255,.85);
  font-variant-numeric: tabular-nums;
  background: rgba(0,0,0,.5); padding: 2px 7px; border-radius: 5px;
  backdrop-filter: blur(3px);
}
.cv-stripe { position: absolute; left: 0; top: 0; bottom: 0; width: 4px; }
.cv-stripe-r { background: var(--red); }
.cv-stripe-b { background: var(--sky); }

/* ═══════════════════════════════════════════════════
   탭2 — CCTV 관리
═══════════════════════════════════════════════════ */
.cv-cam-grid { display: grid; grid-template-columns: 1fr; gap: 5px; padding: 0 5px 5px; }
@media(min-width:520px)  { .cv-cam-grid { grid-template-columns: 1fr 1fr; } }
@media(min-width:900px)  { .cv-cam-grid { grid-template-columns: repeat(3,1fr); } }
@media(min-width:1200px) { .cv-cam-grid { grid-template-columns: repeat(4,1fr); } }

.cv-cam-card {
  background: var(--srf); border: 1px solid var(--brd);
  border-radius: 14px; overflow: hidden; box-shadow: var(--sh-sm);
  display: flex; flex-direction: column;
  transition: transform .18s, box-shadow .18s;
}
.cv-cam-card:hover { transform: translateY(-3px); box-shadow: var(--sh-md); }
.cv-cam-card--off { opacity: .5; }
.cv-cam-vis {
  position: relative; height: 76px;
  background: linear-gradient(135deg,#10142a 0%,#1e2240 100%);
  display: flex; align-items: center; justify-content: center;
}
.cv-cam-num { position: absolute; top: 9px; left: 11px; font-size: .68rem; font-weight: 800; letter-spacing: .07em; color: rgba(255,255,255,.3); font-variant-numeric: tabular-nums; }
.cv-cam-ico-wrap {
  width: 44px; height: 44px; border-radius: 50%;
  background: rgba(79,70,229,.2); border: 1.5px solid rgba(79,70,229,.35);
  display: flex; align-items: center; justify-content: center; color: rgba(255,255,255,.55);
}
.cv-cam-st {
  position: absolute; top: 9px; right: 10px;
  display: inline-flex; align-items: center; gap: 4px;
  font-size: .6rem; font-weight: 800; letter-spacing: .07em; padding: 2px 8px; border-radius: 20px;
}
.cv-st-on  { background: rgba(22,163,74,.18);   color: #4ade80; border: 1px solid rgba(22,163,74,.3); }
.cv-st-off { background: rgba(107,114,128,.12);  color: rgba(255,255,255,.28); border: 1px solid rgba(107,114,128,.18); }
.cv-st-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.cv-st-on .cv-st-dot { animation: cvPulse 1.4s ease-in-out infinite; }
@keyframes cvPulse { 0%,100%{opacity:1} 50%{opacity:.3} }
.cv-cam-body { padding: 11px 13px 6px; flex: 1; }
.cv-cam-name { font-size:.9rem; font-weight:700; color:var(--t1); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-bottom:3px; }
.cv-cam-rtsp { font-size:.7rem; color:var(--t3); font-family:'SF Mono','Menlo',monospace; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-bottom:9px; }
.cv-cam-pills { display:flex; flex-wrap:wrap; gap:4px; }
.cv-pill { display:inline-flex; align-items:center; gap:3px; font-size:.75rem; font-weight:700; padding:2px 7px; border-radius:20px; }
.cv-pill-on  { background:var(--pri-lt); color:var(--pri); }
.cv-pill-off { background:var(--brd-sub); color:var(--t3); }
.cv-cam-edit-btn {
  display:flex; align-items:center; justify-content:center; gap:5px;
  margin:4px 12px 12px; padding:8px; border-radius:9px;
  border:1.5px solid var(--brd); background:var(--bg);
  color:var(--pri); font-size:.8rem; font-weight:700;
  cursor:pointer; transition:all .15s; width:calc(100% - 24px);
}
.cv-cam-edit-btn:hover { background:var(--pri-lt); border-color:var(--pri); }

/* ═══════════════════════════════════════════════════
   탭3 — 분석
═══════════════════════════════════════════════════ */
.cv-stat-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:5px; padding:0 5px 5px; }
@media(min-width:640px) { .cv-stat-grid { grid-template-columns:repeat(4,1fr); } }
.cv-stat-card {
  background:var(--srf); border:1px solid var(--brd); border-radius:12px; padding:14px;
  display:flex; align-items:center; gap:12px; box-shadow:var(--sh-sm);
}
.cv-stat-ico { width:42px; height:42px; border-radius:11px; flex-shrink:0; display:flex; align-items:center; justify-content:center; }
.cv-ico-blu { background:var(--pri-lt); color:var(--pri); }
.cv-ico-red { background:var(--red-lt); color:var(--red); }
.cv-ico-sky { background:var(--sky-lt); color:var(--sky); }
.cv-ico-grn { background:var(--grn-lt); color:var(--grn); }
.cv-stat-n { font-size:1.55rem; font-weight:800; color:var(--t1); line-height:1; font-variant-numeric:tabular-nums; }
.cv-n-red { color:var(--red); }
.cv-n-sky { color:var(--sky); }
.cv-n-grn { color:var(--grn); }
.cv-stat-l { font-size:.72rem; font-weight:600; color:var(--t3); margin-top:3px; }

.cv-chart-card {
  background:var(--srf); border:1px solid var(--brd); border-radius:14px;
  padding: 12px 4px 4px; margin: 5px; box-shadow:var(--sh-sm);
}
.cv-chart-hd {
  display:flex; align-items:center; gap:7px;
  font-size:.84rem; font-weight:700; color:var(--t1); margin-bottom:4px;
}
.cv-chart-ico { color:var(--pri); }
.cv-chart-legend { display:flex; align-items:center; gap:8px; margin-left:auto; }
.cv-legend-dot { width:8px; height:8px; border-radius:50%; display:inline-block; }
.cv-legend-lbl { font-size:.72rem; font-weight:600; color:var(--t3); }
.cv-chart-empty { display:flex; align-items:center; justify-content:center; height:180px; font-size:.82rem; color:var(--t3); }
.cv-chart-row { display:grid; grid-template-columns:1fr; }
@media(min-width:768px) {
  .cv-chart-row { grid-template-columns:1fr 1fr; }
  .cv-chart-row .cv-chart-card { margin:0 7px 14px; }
  .cv-chart-row .cv-chart-card:first-child { margin-left:14px; }
  .cv-chart-row .cv-chart-card:last-child  { margin-right:14px; }
}
.cv-sheet-backdrop {
  position: fixed; inset: 0; z-index: 8000;
  background: rgba(5,8,20,.45);
  backdrop-filter: blur(3px);
  display: flex; align-items: flex-end;
}
.cv-sheet {
  width: 100%; background: var(--srf);
  border-radius: 20px 20px 0 0;
  padding: 12px 20px 32px;
  box-shadow: 0 -8px 32px rgba(0,0,0,.15);
}
.cv-sheet-handle {
  width: 36px; height: 4px; border-radius: 2px;
  background: var(--brd); margin: 0 auto 16px;
}
.cv-sheet-head {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 20px;
}
.cv-sheet-ico { color: var(--pri); }
.cv-sheet-title { font-size: 1rem; font-weight: 800; color: var(--t1); }
.cv-sheet-stats {
  display: flex; align-items: center; justify-content: center;
  gap: 0; margin-bottom: 24px;
  background: var(--bg); border-radius: 14px; padding: 16px 0;
}
.cv-sheet-stat {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; gap: 4px;
}
.cv-sheet-stat-n { font-size: 2rem; font-weight: 900; line-height: 1; font-variant-numeric: tabular-nums; }
.cv-sheet-stat-l { font-size: .72rem; font-weight: 600; color: var(--t3); }
.cv-sheet-stat--red .cv-sheet-stat-n { color: var(--red); }
.cv-sheet-stat--blu .cv-sheet-stat-n { color: var(--sky); }
.cv-sheet-divider { width: 1px; height: 40px; background: var(--brd); }
.cv-sheet-actions { display: flex; gap: 8px; }

/* 트랜지션 */
.cv-sheet-t-enter-active, .cv-sheet-t-leave-active { transition: opacity .25s; }
.cv-sheet-t-enter-active .cv-sheet, .cv-sheet-t-leave-active .cv-sheet {
  transition: transform .3s cubic-bezier(.25,.46,.45,.94);
}
.cv-sheet-t-enter .cv-sheet, .cv-sheet-t-leave-to .cv-sheet { transform: translateY(100%); }
.cv-sheet-t-enter, .cv-sheet-t-leave-to { opacity: 0; }

/* ═══════════════════════════════════════════════════
   CCTV 편집 모달
═══════════════════════════════════════════════════ */
.cv-backdrop {
  position:fixed; inset:0; z-index:9000;
  background:rgba(5,8,20,.55); backdrop-filter:blur(5px);
  display:flex; align-items:flex-end; justify-content:center;
}
@media(min-width:600px) { .cv-backdrop { align-items:center; padding:20px; } }
.cv-modal {
  width:100%; max-width:460px; background:var(--srf);
  border-radius:20px 20px 0 0; box-shadow:0 -12px 48px rgba(0,0,0,.18); overflow:hidden;
}
@media(min-width:600px) { .cv-modal { border-radius:20px; box-shadow:var(--sh-md); } }
.cv-modal-hd { display:flex; align-items:center; justify-content:space-between; padding:16px 18px 12px; border-bottom:1px solid var(--brd-sub); }
.cv-modal-hd-l { display:flex; align-items:center; gap:9px; }
.cv-modal-ico { width:30px; height:30px; border-radius:8px; background:var(--pri-lt); color:var(--pri); display:flex; align-items:center; justify-content:center; }
.cv-modal-ttl { font-size:.95rem; font-weight:800; color:var(--t1); }
.cv-modal-x { width:30px; height:30px; border-radius:8px; border:none; background:var(--brd-sub); color:var(--t2); display:flex; align-items:center; justify-content:center; cursor:pointer; transition:background .15s; }
.cv-modal-x:hover { background:var(--brd); }
.cv-modal-cam-tag { margin:12px 18px 0; padding:10px 14px; background:var(--pri-lt); color:#3730a3; border-radius:10px; font-size:.84rem; font-weight:700; display:flex; align-items:center; }
.cv-modal-err { margin:9px 18px 0; padding:9px 14px; background:var(--red-lt); color:#b91c1c; border-radius:8px; font-size:.82rem; font-weight:600; display:flex; align-items:center; }
.cv-modal-bd { padding:14px 18px 0; }
.cv-field { margin-bottom:14px; }
.cv-flbl { display:block; font-size:.7rem; font-weight:800; color:var(--t3); margin-bottom:5px; letter-spacing:.06em; text-transform:uppercase; }
.cv-finp { width:100%; padding:10px 13px; border:1.5px solid var(--brd); border-radius:10px; font-size:.88rem; color:var(--t1); background:var(--bg); transition:border-color .15s, box-shadow .15s; }
.cv-finp:focus { outline:none; border-color:var(--pri); box-shadow:0 0 0 3px rgba(79,70,229,.1); background:#fff; }
.cv-finp-mono { font-family:'SF Mono','Menlo',monospace; font-size:.8rem; }
.cv-tgl-list { border:1.5px solid var(--brd-sub); border-radius:12px; overflow:hidden; }
.cv-tgl-row { display:flex; align-items:center; justify-content:space-between; padding:11px 14px; border-bottom:1px solid var(--brd-sub); transition:background .12s; }
.cv-tgl-row:last-child { border-bottom:none; }
.cv-tgl-row:hover { background:var(--bg); }
.cv-tgl-name { font-size:.85rem; font-weight:700; color:var(--t1); }
.cv-tgl-hint { font-size:.71rem; color:var(--t3); margin-top:1px; }
.cv-sw { position:relative; width:44px; height:24px; cursor:pointer; flex-shrink:0; }
.cv-sw input { position:absolute; inset:0; opacity:0; width:100%; height:100%; cursor:pointer; margin:0; }
.cv-sw-tr { position:absolute; inset:0; background:#d1d5db; border-radius:20px; transition:background .2s; }
.cv-sw-th { display:block; position:absolute; width:18px; height:18px; left:3px; top:3px; background:#fff; border-radius:50%; box-shadow:0 1px 4px rgba(0,0,0,.2); transition:transform .2s; }
.cv-sw input:checked ~ .cv-sw-tr { background:var(--pri); }
.cv-sw input:checked ~ .cv-sw-tr .cv-sw-th { transform:translateX(20px); }
.cv-modal-ft { display:flex; gap:8px; padding:14px 18px 22px; border-top:1px solid var(--brd-sub); }
@media(min-width:600px) { .cv-modal-ft { padding-bottom:14px; } }
.cv-btn { flex:1; padding:11px; border-radius:10px; border:none; font-size:.88rem; font-weight:800; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background .15s; }
.cv-btn-ghost { background:var(--brd-sub); color:var(--t2); }
.cv-btn-ghost:hover { background:var(--brd); }
.cv-btn-pri { background:var(--pri); color:#fff; }
.cv-btn-pri:hover { background:#3d35c8; }
.cv-btn-pri:disabled { opacity:.6; cursor:not-allowed; }
.cv-btn-spin { width:15px; height:15px; margin-right:7px; border:2px solid rgba(255,255,255,.4); border-top-color:#fff; border-radius:50%; animation:cvSpin .65s linear infinite; }
.cv-modal-t-enter-active, .cv-modal-t-leave-active { transition:opacity .2s; }
.cv-modal-t-enter, .cv-modal-t-leave-to { opacity:0; }

/* ═══════════════════════════════════════════════════
   전체화면 뷰어
═══════════════════════════════════════════════════ */
.cv-fs { position:fixed; inset:0; z-index:9999; background:rgba(0,0,0,.96); display:flex; align-items:center; justify-content:center; }
.cv-fs-x { position:absolute; top:16px; right:16px; width:40px; height:40px; border-radius:50%; border:none; background:rgba(255,255,255,.12); color:#fff; display:flex; align-items:center; justify-content:center; cursor:pointer; z-index:10; transition:background .15s; }
.cv-fs-x:hover { background:rgba(255,255,255,.22); }
.cv-fs-inner { display:flex; flex-direction:column; align-items:center; width:100%; overflow:hidden; }
.cv-fs-media { max-width:100vw; max-height:82vh; object-fit:contain; display:block; border-radius:6px; }
.cv-fs-info { display:flex; align-items:center; gap:10px; flex-wrap:wrap; padding:12px 16px; justify-content:center; }
.cv-fs-cam, .cv-fs-time, .cv-fs-cnt { font-size:1rem; color:rgba(255,255,255,.6); font-variant-numeric:tabular-nums; }
.cv-fs-nav { position:absolute; top:50%; transform:translateY(-50%); width:44px; height:44px; border-radius:50%; border:none; background:rgba(255,255,255,.1); color:#fff; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:background .15s; }
.cv-fs-nav:disabled { opacity:.25; cursor:default; }
.cv-fs-nav:not(:disabled):hover { background:rgba(255,255,255,.22); }
.cv-fs-prev { left:14px; }
.cv-fs-next { right:14px; }
.cv-fsl-enter-active, .cv-fsl-leave-active, .cv-fsr-enter-active, .cv-fsr-leave-active { transition:transform .25s ease, opacity .25s ease; }
.cv-fsl-enter    { transform:translateX(60px);  opacity:0; }
.cv-fsl-leave-to { transform:translateX(-60px); opacity:0; }
.cv-fsr-enter    { transform:translateX(-60px); opacity:0; }
.cv-fsr-leave-to { transform:translateX(60px);  opacity:0; }
.cv-fs-t-enter-active, .cv-fs-t-leave-active { transition:opacity .2s; }
.cv-fs-t-enter, .cv-fs-t-leave-to { opacity:0; }

/* ═══════════════════════════════════════════════════
   PC 최적화
═══════════════════════════════════════════════════ */
@media(min-width:1200px) {
  .cv-filter-bar  { padding:10px 24px; }
  .cv-slide-wrap  { padding:12px 24px 0; }
  .cv-pager       { padding:14px 24px 6px; }
  .cv-sec-hd      { padding:20px 24px 14px; }
  .cv-cam-grid    { padding:0 24px 24px; }
  .cv-stat-grid   { padding:0 24px 16px; }
  .cv-chart-card  { margin:0 24px 16px; }
  .cv-chart-row .cv-chart-card              { margin:0 8px 16px; }
  .cv-chart-row .cv-chart-card:first-child  { margin-left:24px; }
  .cv-chart-row .cv-chart-card:last-child   { margin-right:24px; }
}
</style>
